import Flickity from 'flickity';
import isMobile from 'is-mobile';
import { add, toggle, listen, qs, qsa, remove } from '@fluorescent/dom';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { createFocusTrap } from 'focus-trap';

const selectors = {
  close: '[data-close]',
  slider: '[data-slider]',
  slide: '[data-slide]',
  imageById: id => `[data-id='${id}']`,
  navItem: '[data-nav-item]',
  wrapper: '.lightbox__images-wrapper',
  prevButton: '[data-prev]',
  nextButton: '[data-next]',
};

const classes = {
  visible: 'visible',
  active: 'active',
  zoom: 'zoom',
};

export default function Lightbox(node) {
  const trap = createFocusTrap(node);
  const navItems = qsa(selectors.navItem, node);
  const wrapper = qs(selectors.wrapper, node);
  const images = qsa(selectors.slide, node);
  const previousButton = qs(selectors.prevButton, node);
  const nextButton = qs(selectors.nextButton, node);
  const sliderContainer = qs(selectors.slider, node);

  const slider = new Flickity(sliderContainer, {
    adaptiveHeight: true,
    draggable: isMobile({ tablet: true, featureDetect: true }),
    prevNextButtons: false,
    wrapAround: false,
    pageDots: false,
  });

  if (images.length > 1) {
    slider.on('scroll', progress => {
      _resetZoom();
      const progressScale = progress * 100;

      // https://github.com/metafizzy/flickity/issues/289
      previousButton.disabled = progressScale < 1;
      nextButton.disabled = progressScale > 99;
    });

    slider.on('select', () => {
      navItems.forEach(item => remove(item, classes.active));
      add(navItems[slider.selectedIndex], classes.active);
      navItems[slider.selectedIndex].scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
      });
    });
  } else {
    add(previousButton, 'hidden');
    add(nextButton, 'hidden');
    previousButton.disabled = true;
    nextButton.disabled = true;
  }

  const events = [
    listen(qs(selectors.close, node), 'click', e => {
      e.preventDefault();
      close();
    }),
    listen(node, 'keydown', ({ keyCode }) => {
      if (keyCode === 27) close();
    }),
    listen(navItems, 'click', e => {
      e.preventDefault();

      const { index } = e.currentTarget.dataset;
      slider.select(index);
    }),
    listen(images, 'click', e => {
      e.preventDefault();

      _handleZoom(e);
    }),
    listen(previousButton, 'click', () => slider.previous()),
    listen(nextButton, 'click', () => slider.next()),
  ];

  function _handleZoom(event) {
    const image = event.currentTarget;
    const zoomed = image.classList.contains(classes.zoom);
    toggle(image, classes.zoom, !zoomed);

    if (zoomed) {
      _resetZoom(image);
      return;
    }

    const x = event.clientX;

    const y = event.clientY + wrapper.scrollTop - sliderContainer.offsetTop;

    const xDelta = (x - image.clientWidth / 2) * -1;
    const yDelta = (y - image.clientHeight / 2) * -1;

    image.style.transform = `translate3d(${xDelta}px, ${yDelta}px, 0) scale(2)`;
  }

  function _resetZoom(image) {
    if (image) {
      remove(image, classes.zoom);
      image.style.transform = `translate3d(0px, 0px, 0) scale(1)`;

      return;
    }

    images.forEach(image => {
      remove(image, classes.zoom);
      image.style.transform = `translate3d(0px, 0px, 0) scale(1)`;
    });
  }

  function open(id) {
    add(node, classes.active);
    setTimeout(() => {
      add(node, classes.visible);
      disableBodyScroll(node, {
        allowTouchMove: el => {
          while (el && el !== document.body) {
            if (el.getAttribute('data-scroll-lock-ignore') !== null) {
              return true;
            }
            el = el.parentNode;
          }
        },
        reserveScrollBarGap: true,
      });
      trap.activate();

      const image = qs(selectors.imageById(id), node);
      const { slideIndex } = image.dataset;

      slider.select(slideIndex, false, true);
    }, 50);
  }

  function close() {
    _resetZoom();
    remove(node, classes.visible);
    setTimeout(() => {
      remove(node, classes.active);

      enableBodyScroll(node);
      trap.deactivate();
    }, 300);
  }

  function destroy() {
    events.forEach(unsubscribe => unsubscribe());
    slider.destroy();
  }

  return { destroy, open };
}
